// Version: 1.4
// v1.4: Number tiles use the shared .stat-card; inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.3: Removed an unused tab style left over from before the shared tabs.
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';
import './Admin.css';

const CONF_OPTIONS = ['high', 'medium', 'low'];
const CONF_COLOR   = { high: 'var(--green)', medium: 'var(--amber)', low: 'var(--text-muted)' };
const SIG_TYPES    = ['spf_include', 'mx_pattern', 'txt_pattern', 'cname_pattern'];

const SIG_LABELS = {
  spf_include:   'SPF include:',
  mx_pattern:    'MX record',
  txt_pattern:   'TXT record',
  cname_pattern: 'CNAME',
};

const DEFAULT_CATEGORIES = [
  'Analytics and Reporting',
  'Cloud Storage and File Sharing',
  'Communication and Messaging',
  'CRM and Sales',
  'Customer Support',
  'Design and Creative',
  'Development Tools',
  'Email and Marketing',
  'Finance and Accounting',
  'HR and People',
  'Identity and Access Management',
  'IT and Infrastructure',
  'Legal and Compliance',
  'Operations and ERP',
  'Project Management',
  'Security',
  'Video Conferencing',
];

function Badge({ color, children }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      color, background: `${color}18`, border: `1px solid ${color}40`,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {children}
    </span>
  );
}

function Flash({ messages }) {
  if (!messages.length) return null;
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {messages.map(m => (
        <div key={m.id} style={{
          background: m.ok ? 'var(--green)' : 'var(--red)',
          color: '#000', padding: '10px 16px', borderRadius: 8,
          fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }}>{m.msg}</div>
      ))}
    </div>
  );
}

function PromoteModal({ item, initialFp, categories = [], onClose, onSaved }) {
  const editMode = !!initialFp;

  function toTitleCase(s) {
    return s.replace(/\b\w/g, c => c.toUpperCase());
  }

  const [name,       setName]       = useState(editMode ? initialFp.name       : (item ? toTitleCase(item.apex_domain.replace(/\./g, ' ')) : ''));
  const [category,   setCategory]   = useState(editMode ? (initialFp.category || '') : '');
  const [hq,         setHq]         = useState(editMode ? (initialFp.hq || '')        : '');
  const [confidence, setConfidence] = useState(editMode ? initialFp.confidence         : 'medium');
  const [signals,    setSignals]    = useState(() => {
    if (editMode) return initialFp.signals?.length ? initialFp.signals : [{ type: 'spf_include', pattern: '' }];
    if (item)     return [{ type: item.type, pattern: item.apex_domain }];
    return [{ type: 'spf_include', pattern: '' }];
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // Merge default categories with existing library categories, deduped and sorted
  const allCategories = [...new Set([...DEFAULT_CATEGORIES, ...categories])].sort();

  function addSignal() {
    setSignals(s => [...s, { type: 'spf_include', pattern: '' }]);
  }

  function removeSignal(i) {
    setSignals(s => s.filter((_, idx) => idx !== i));
  }

  function updateSignal(i, field, value) {
    setSignals(s => s.map((sig, idx) => idx === i ? { ...sig, [field]: value } : sig));
  }

  async function handleSave() {
    if (!name.trim())     return setError('Name is required');
    if (!category.trim()) return setError('Category is required');
    const validSigs = signals.filter(s => s.pattern.trim());
    if (!validSigs.length) return setError('At least one signal pattern is required');

    setSaving(true);
    setError('');
    const id     = editMode ? initialFp.id : name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const url    = editMode ? `/api/saas/fingerprints/${id}` : '/api/saas/fingerprints';
    const method = editMode ? 'PUT' : 'POST';
    try {
      const r = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name, category, hq, confidence, signals: validSigs }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed to save'); setSaving(false); return; }
      onSaved(name, editMode);
    } catch {
      setError('Network error');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 520, width: '100%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{editMode ? 'Edit Fingerprint' : 'Add to Fingerprint Library'}</div>

        {item && !editMode && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12,
          }}>
            <span style={{ color: 'var(--text-muted)', marginRight: 8 }}>Unmatched signal:</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{item.value}</span>
            <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 11 }}>
              ({SIG_LABELS[item.type] || item.type}) · seen in {item.count} client{item.count !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Service Name *</label>
              <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. HubSpot" />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Category *</label>
              <input
                className="form-input"
                list="saas-categories"
                value={category}
                onChange={e => setCategory(e.target.value)}
                placeholder="Select or type a category…"
              />
              <datalist id="saas-categories">
                {allCategories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>HQ Location</label>
              <input className="form-input" value={hq} onChange={e => setHq(e.target.value)} placeholder="e.g. Cambridge, MA, USA" />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Confidence</label>
              <select className="form-input" value={confidence} onChange={e => setConfidence(e.target.value)}>
                {CONF_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Detection Signals *</label>
              <button className="btn btn-secondary btn-sm" onClick={addSignal}>+ Add signal</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {signals.map((sig, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select
                    className="form-input input-sm"
                    style={{ flex: '0 0 140px' }}
                    value={sig.type}
                    onChange={e => updateSignal(i, 'type', e.target.value)}
                  >
                    {SIG_TYPES.map(t => <option key={t} value={t}>{SIG_LABELS[t]}</option>)}
                  </select>
                  <input
                    className="form-input input-sm"
                    style={{ flex: 1 }}
                    value={sig.pattern}
                    onChange={e => updateSignal(i, 'pattern', e.target.value)}
                    placeholder="e.g. hubspot.com"
                  />
                  {signals.length > 1 && (
                    <button className="btn btn-secondary btn-sm" onClick={() => removeSignal(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {error && <div style={{ fontSize: 12, color: 'var(--red)', padding: '8px 12px', background: 'var(--red)10', borderRadius: 6 }}>{error}</div>}
        </div>

        <div className="modal-actions" style={{ marginTop: 20 }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editMode ? 'Save Changes' : 'Add to Library'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function FingerprintRow({ fp, onDelete, onEdit }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting,   setDeleting]   = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      const r = await authFetch(`/api/saas/fingerprints/${fp.id}`, { method: 'DELETE' });
      if (r.ok) onDelete(fp.id);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '10px 0', borderBottom: '1px solid var(--border)', gap: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fp.name}</span>
          <Badge color={CONF_COLOR[fp.confidence] || 'var(--text-muted)'}>
            {fp.confidence ? fp.confidence.charAt(0).toUpperCase() + fp.confidence.slice(1) : '—'}
          </Badge>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fp.category}</span>
        </div>
        {fp.hq && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fp.hq}</div>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          {(fp.signals || []).map((s, i) => (
            <span key={i} style={{
              fontSize: 10, fontFamily: 'var(--font-mono)', background: 'var(--surface)',
              border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', color: 'var(--text-muted)',
            }}>
              {SIG_LABELS[s.type] || s.type} {s.pattern}
            </span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {confirming ? (
          <>
            <button className="btn btn-secondary btn-sm" onClick={handleDelete} disabled={deleting}>
              {deleting ? '…' : 'Confirm'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
          </>
        ) : (
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => onEdit(fp)}>Edit</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(true)}>Remove</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SaasReview() {
  const [unmatched,     setUnmatched]     = useState([]);
  const [fingerprints,  setFingerprints]  = useState([]);
  const [loadingU,      setLoadingU]      = useState(true);
  const [loadingF,      setLoadingF]      = useState(true);
  const [tab,           setTab]           = useState('unmatched');
  const [promoting,     setPromoting]     = useState(null);
  const [editingFp,     setEditingFp]     = useState(null);
  const [showPromote,   setShowPromote]   = useState(false);
  const [messages,      setMessages]      = useState([]);
  const [fpSearch,      setFpSearch]      = useState('');
  const [umSearch,      setUmSearch]      = useState('');

  const flash = (msg, ok = true) => {
    const id = Date.now();
    setMessages(m => [...m, { id, msg, ok }]);
    setTimeout(() => setMessages(m => m.filter(x => x.id !== id)), 4000);
  };

  const fetchUnmatched = useCallback(() => {
    setLoadingU(true);
    authFetch('/api/saas/unmatched')
      .then(r => r.json())
      .then(d => { setUnmatched(d.unmatched || []); setLoadingU(false); })
      .catch(() => setLoadingU(false));
  }, []);

  const fetchFingerprints = useCallback(() => {
    setLoadingF(true);
    authFetch('/api/saas/fingerprints')
      .then(r => r.json())
      .then(d => { setFingerprints(d.fingerprints || []); setLoadingF(false); })
      .catch(() => setLoadingF(false));
  }, []);

  useEffect(() => { fetchUnmatched(); fetchFingerprints(); }, [fetchUnmatched, fetchFingerprints]);

  // Derive existing categories from fingerprint library for datalist
  const existingCategories = [...new Set(fingerprints.map(fp => fp.category).filter(Boolean))];

  function closeModal() {
    setShowPromote(false);
    setPromoting(null);
    setEditingFp(null);
  }

  function handleSaved(name, isEdit) {
    closeModal();
    flash(isEdit ? `'${name}' updated` : `'${name}' added to fingerprint library`);
    fetchFingerprints();
    if (!isEdit) fetchUnmatched();
  }

  function handleDeleted(id) {
    setFingerprints(f => f.filter(fp => fp.id !== id));
    flash('Fingerprint removed');
  }

  function handleEdit(fp) {
    setEditingFp(fp);
    setPromoting(null);
    setShowPromote(true);
  }

  const filteredFP = fingerprints.filter(fp =>
    !fpSearch || fp.name.toLowerCase().includes(fpSearch.toLowerCase()) ||
    fp.category.toLowerCase().includes(fpSearch.toLowerCase())
  );

  const filteredUM = unmatched.filter(item =>
    !umSearch || item.apex_domain.toLowerCase().includes(umSearch.toLowerCase()) ||
    item.value.toLowerCase().includes(umSearch.toLowerCase())
  );


  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <Flash messages={messages} />

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', margin: 0 }}>SaaS Discovery</h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          Review unrecognised DNS signals from client scans and manage the fingerprint library.
        </p>
      </div>

      {/* Stats row */}
      <div className="stat-tiles">
        {[
          { label: 'Fingerprints',       value: fingerprints.length, tone: 'accent' },
          { label: 'Unmatched signals',  value: unmatched.length,    tone: unmatched.length > 0 ? 'amber' : 'green' },
          { label: 'Clients scanned',    value: [...new Set(unmatched.flatMap(u => u.clients.map(c => c.slug)))].length, tone: 'muted' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className={`stat-value ${s.tone}`}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--border)', display: 'flex', gap: 4, marginBottom: 20 }}>
        <button className="tab-btn" aria-selected={tab === 'unmatched'} onClick={() => setTab('unmatched')}>
          Unmatched Signals {unmatched.length > 0 && <span style={{ background: 'var(--amber)', color: '#000', borderRadius: 20, fontSize: 10, padding: '1px 6px', marginLeft: 6, fontWeight: 700 }}>{unmatched.length}</span>}
        </button>
        <button className="tab-btn" aria-selected={tab === 'library'} onClick={() => setTab('library')}>
          Fingerprint Library <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>({fingerprints.length})</span>
        </button>
      </div>

      {/* ── Unmatched Signals tab ── */}
      {tab === 'unmatched' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
            <input
              className="form-input input-sm"
              style={{ maxWidth: 280 }}
              placeholder="Filter by domain…"
              value={umSearch}
              onChange={e => setUmSearch(e.target.value)}
            />
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
              Signals that appeared in client scans but didn't match any fingerprint. Promote them to teach the scanner.
            </p>
          </div>

          {loadingU ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><span className="spinner" /></div>
          ) : filteredUM.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {umSearch ? 'No signals match your filter.' : '✓ No unmatched signals — fingerprint library is up to date.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredUM.map(item => (
                <div key={item.apex_domain} style={{
                  background: 'var(--card-bg)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: '14px 16px',
                  display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                        {item.apex_domain}
                      </span>
                      <Badge color="var(--amber)">
                        {SIG_LABELS[item.type] || item.type}
                      </Badge>
                      {item.count > 1 && (
                        <Badge color="var(--accent)">{item.count} clients</Badge>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>
                      {item.value}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {item.clients.map(c => (
                        <span key={c.slug} style={{
                          fontSize: 10, background: 'var(--surface)', border: '1px solid var(--border)',
                          borderRadius: 4, padding: '1px 6px', color: 'var(--text-muted)',
                        }}>{c.name}</span>
                      ))}
                    </div>
                  </div>
                  <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}
                   
                   
                    onClick={() => { setPromoting(item); setEditingFp(null); setShowPromote(true); }}
                  >
                    + Add to Library
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Library tab ── */}
      {tab === 'library' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
            <input
              className="form-input input-sm"
              style={{ maxWidth: 280 }}
              placeholder="Filter by name or category…"
              value={fpSearch}
              onChange={e => setFpSearch(e.target.value)}
            />
            <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}
             
             
              onClick={() => { setPromoting(null); setEditingFp(null); setShowPromote(true); }}
            >
              + New Fingerprint
            </button>
          </div>

          {loadingF ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><span className="spinner" /></div>
          ) : (
            (() => {
              const byCategory = {};
              filteredFP.forEach(fp => {
                const cat = fp.category || 'Uncategorised';
                if (!byCategory[cat]) byCategory[cat] = [];
                byCategory[cat].push(fp);
              });

              if (!Object.keys(byCategory).length) {
                return (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    {fpSearch ? 'No fingerprints match your filter.' : 'No fingerprints in library yet.'}
                  </div>
                );
              }

              return Object.keys(byCategory).sort().map(cat => (
                <div key={cat} style={{ marginBottom: 20 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6,
                  }}>{cat} <span style={{ fontWeight: 400 }}>({byCategory[cat].length})</span></div>
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 14px' }}>
                    {byCategory[cat].map(fp => (
                      <FingerprintRow key={fp.id} fp={fp} onDelete={handleDeleted} onEdit={handleEdit} />
                    ))}
                  </div>
                </div>
              ));
            })()
          )}
        </div>
      )}

      {/* Add / Edit fingerprint modal */}
      {showPromote && (
        <PromoteModal
          item={promoting}
          initialFp={editingFp}
          categories={existingCategories}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
